import { PURMO_COMPACT, type PanelRadiatorType } from "./emitter-catalogue.js";
import type { BarrierType, LineOfSight } from "./noise.js";

/**
 * What a designer can draw on the floor plan, and the REAL sizes it is drawn at.
 *
 * Asked for on 2026-09-10: "add option to add objects to floor plan, objects such
 * as new heat-pump, add piping, add connectors, and get actual size for heat-pump,
 * for radiators add few different sizes in the object section to select, such as
 * k2, k3, 600x1200 etc."
 *
 * One vocabulary, shared by the API (which validates every object against it
 * before storing it — migration 0032) and the portal (which offers it in the
 * plan's Objects palette and draws with it), so the two cannot disagree about
 * what a "Vaillant aroTHERM plus 7 kW" measures or which radiator sizes exist.
 *
 * ---------------------------------------------------------------------------
 * A LAYOUT DRAWING, NOT A SIZING RECORD
 * ---------------------------------------------------------------------------
 * Nothing in this file carries a heat output, a capacity or an efficiency. The
 * heat pump a design is sized and certificated against is the one recorded in
 * the design decisions (`heat-pump-catalogue.ts`, the design-decisions routes),
 * and emitter outputs come from the radiator catalogues through
 * `selectEmitters`. The "7 kW" in a model name below is the manufacturer's
 * marketing SIZE — part of the name on the box — and is never a number any
 * calculation reads.
 *
 * One exception, added 2026-09-10 for the MCS design check on the plan: a
 * placed outdoor unit may carry the DESIGNER's inputs for it (`design` on
 * `HeatPumpSpec` — capacity at design conditions, sound power, noise
 * assessment positions), each with where it came from. They are the
 * designer's figures, typed or accepted from a sourced pre-fill; nothing in
 * this file supplies them.
 *
 * ---------------------------------------------------------------------------
 * SOURCING RULE
 * ---------------------------------------------------------------------------
 * Every manufacturer dimension in this file sits next to where it came from: the
 * URL of the datasheet or manual it was read from, the document's name and
 * revision where it prints one, where in the document, and the date it was
 * accessed. A model whose dimensions could not be read from a manufacturer
 * document is LEFT OUT rather than guessed. A figure from a retailer or
 * distributor is marked `kind: "secondary"` and says so in the drawing's caption.
 *
 * Radiator depths and sizes are not re-typed here at all: they are read out of
 * `PURMO_COMPACT` in `emitter-catalogue.ts`, which already carries its own
 * provenance (Purmo GB catalogue, July 2024, fetched from purmo.com), so there
 * is exactly one transcription of those numbers in the repository.
 */

// ------------------------------------------------------------ object model --

export const PLAN_OBJECT_KINDS = ["heat-pump", "radiator", "pipe", "fitting", "cylinder", "buffer"] as const;
export type PlanObjectKind = (typeof PLAN_OBJECT_KINDS)[number];

/** A point on the plan, in metres, in the scan's own frame (x = world X, y = world Z). */
export interface PlanObjectPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Where a placed object sits: its CENTRE, in plan metres, and its rotation in
 * degrees clockwise on screen (the plan's y axis points down the screen). At 0°
 * an object's width/length runs along +x and its depth along +y.
 */
export interface PlacedGeometry {
  readonly x: number;
  readonly y: number;
  readonly rotationDeg: number;
}

/** A pipe run: the points it passes through, in order. At least two. */
export interface RunGeometry {
  readonly points: readonly PlanObjectPoint[];
}

export interface HeatPumpSpec {
  /** `OutdoorUnit.id` for a catalogue model; null for a unit sized by hand. */
  readonly catalogueId: string | null;
  /** "Vaillant aroTHERM plus 7 kW", or the designer's own name for a custom unit. */
  readonly name: string;
  /** Casing width, mm — the long side seen from the front. */
  readonly widthMm: number;
  /** Casing depth, mm — front to back. */
  readonly depthMm: number;
  /** Casing height, mm. Not drawn on a plan; shown in the caption. */
  readonly heightMm: number;
  /**
   * The designer's inputs to the MCS design check for this unit — capacity at
   * design conditions, sound power, placement facts and the MCS 020 a)
   * assessment positions. Every figure here is the DESIGNER's, typed or
   * accepted from a sourced pre-fill; each says where it came from. Absent
   * until the designer enters something.
   */
  readonly design?: HeatPumpDesignInputs;
}

// ------------------------------------------------ heat pump design inputs --

/** How the unit stands: on the ground (or a plinth), or on wall brackets clear of it. */
export const HEAT_PUMP_MOUNTINGS = ["ground", "wall-bracket"] as const;
export type HeatPumpMounting = (typeof HEAT_PUMP_MOUNTINGS)[number];

export const NOISE_BARRIER_TYPES = ["none", "type-1", "type-2"] as const satisfies readonly BarrierType[];
export const NOISE_LINES_OF_SIGHT = ["full-view", "partial-view", "no-view"] as const satisfies readonly LineOfSight[];

/**
 * One MCS 020 a) assessment position: 1 m outside the centre of a neighbour's
 * habitable-room window or door (the standard's definition). Either placed on
 * the plan (`point`, plan metres — it may lie outside the scanned area) or
 * given as a measured straight-line distance (`distanceM`); a typed distance
 * wins over the plan's when both are present.
 */
export interface NoiseAssessmentPoint {
  /** Stable within the unit's list; chosen by the client. */
  readonly id: string;
  /** "14 Acacia Ave, first-floor rear bedroom window" — MCS 020 a) Step 2 wants the address, window and floor. */
  readonly description: string;
  readonly point: PlanObjectPoint | null;
  readonly distanceM: number | null;
  /** MCS 020 a) Step 6. "none" and "full-view" are the louder, uncorrected case. */
  readonly barrierType: (typeof NOISE_BARRIER_TYPES)[number];
  readonly lineOfSight: (typeof NOISE_LINES_OF_SIGHT)[number];
}

/** Manufacturer's minimum clearances round the unit, mm, as the designer read them from the manual. */
export interface ClearancesMm {
  readonly back?: number;
  readonly front?: number;
  readonly left?: number;
  readonly right?: number;
}

export interface HeatPumpDesignInputs {
  /**
   * Heating output at the design external temperature and the design flow
   * temperature, W, WITHOUT any back-up heater (MIS 3005-D §3.4.1(e)).
   */
  readonly capacityAtDesignW?: number;
  /** Where the figure came from — the MCS Product Directory, or the datasheet pre-fill's citation. */
  readonly capacitySource?: string;
  /**
   * The conditions the capacity was entered for. If the design flow or the
   * design external temperature changes afterwards, the check says the
   * capacity no longer matches rather than using it at the new conditions.
   */
  readonly capacityConditions?: { readonly outdoorC: number; readonly flowC: number };
  /** A-weighted sound POWER level, dB(A), EN 12102 — never a sound pressure, never low-noise mode (MCS 020 a) Step 3). */
  readonly soundPowerDbA?: number;
  readonly soundPowerSource?: string;
  /** Absent means not stated: the check then assumes "ground" — one more reflecting surface, the louder case — and says so. */
  readonly mounting?: HeatPumpMounting;
  /**
   * The thickness of the wall the unit stands against, mm. The scan sees only
   * the room side of a wall; with this, the gap behind the unit and the 1 m
   * reflecting-surface test are measured to the wall's outside face.
   */
  readonly wallThicknessMm?: number;
  /** The designer's own directivity factor Q (MCS 020 a) Step 4), overriding the one read from the plan. */
  readonly directivityOverride?: 2 | 4 | 8;
  /** The designer's clearances, used where the manufacturer's are not in the datasheet data (or to replace them). */
  readonly clearancesMm?: ClearancesMm;
  readonly clearancesSource?: string;
  readonly noisePositions?: readonly NoiseAssessmentPoint[];
}

/** Bounds on the design inputs. They catch typos (kW for W, metres for mm), not products. */
export const HEAT_PUMP_DESIGN_LIMITS = {
  capacityW: { min: 500, max: 100_000 },
  soundPowerDbA: { min: 20, max: 100 },
  wallThicknessMm: { min: 50, max: 1000 },
  clearanceMm: { min: 0, max: 10_000 },
  distanceM: { min: 0.1, max: 1000 },
  conditionC: { min: -40, max: 90 },
  maxNoisePositions: 20,
  // Long enough for a datasheet citation with its URL.
  maxText: 1000,
} as const;

export interface RadiatorSpec {
  /** Which range's figures the depth is nominal for. One range today. */
  readonly range: typeof RADIATOR_PLAN_RANGE.id;
  readonly panelType: PanelRadiatorType;
  readonly heightMm: number;
  readonly lengthMm: number;
  /** Nominal depth for this panel type in `range`. */
  readonly depthMm: number;
  /**
   * Floor to the bottom of the radiator, mm, where the designer has set it.
   * Absent means "not decided": the plan's fit checks then assume
   * `DEFAULT_RADIATOR_MOUNTING_HEIGHT_MM` and say that they have.
   */
  readonly mountingHeightMm?: number;
}

/**
 * The floor-to-underside gap a radiator is assumed to be hung at when the
 * designer has not said. An ASSUMPTION, not a sourced figure: 150 mm is a
 * common allowance for valves and cleaning underneath, and it is shown as
 * assumed wherever it is used (the sill check on the floor plan). Set the
 * real figure on the radiator when it is known.
 */
export const DEFAULT_RADIATOR_MOUNTING_HEIGHT_MM = 150;

/** What a stored mounting height may be, mm. Catches typos (metres, or a whole wall), not practice. */
export const RADIATOR_MOUNTING_HEIGHT_LIMITS_MM = { min: 0, max: 2000 } as const;

/** The mounting height a check should use, and whether it is the designer's or the assumption. */
export function radiatorMountingHeight(spec: Pick<RadiatorSpec, "mountingHeightMm">): {
  readonly mm: number;
  readonly assumed: boolean;
} {
  return spec.mountingHeightMm === undefined
    ? { mm: DEFAULT_RADIATOR_MOUNTING_HEIGHT_MM, assumed: true }
    : { mm: spec.mountingHeightMm, assumed: false };
}

/**
 * Pipe sizes, by nominal outside diameter (the size a UK installer orders by —
 * "22 mm"). A label and a line weight on the drawing, not a figure the plan is
 * scaled by.
 *
 * 35 mm added 2026-09-11: MCS's own worked example needs 35 mm to carry about
 * 16.75 kW at a 5 K flow–return difference (research note
 * docs/research/installer-pack-2026-09-10.md, "Gaps"). The sizes are copper's
 * (BS EN 1057); PEX barrier pipe is made to the same outside diameters, and a
 * multilayer run is drawn at the copper size it replaces — see
 * `PLAN_PIPE_MATERIALS`.
 */
export const PIPE_DIAMETERS_MM = [10, 15, 22, 28, 35] as const;
export type PipeDiameterMm = (typeof PIPE_DIAMETERS_MM)[number];

export const PIPE_CIRCUITS = ["flow", "return"] as const;
export type PipeCircuit = (typeof PIPE_CIRCUITS)[number];

/**
 * What a drawn run is made of. Added 2026-09-11 (research note, "Gaps": "No
 * pipe material on plan pipes").
 *
 *   copper       BS EN 1057 tube, bought in straight lengths.
 *   pex-barrier  PE-X with an oxygen barrier (JG Speedfit, Hep2O …), bought in
 *                coils or straight lengths, at copper's outside diameters.
 *   multilayer   PE-X/aluminium/PE-X composite. Its ranges are sized
 *                differently (16/20/26/32 mm) — on the plan it is drawn at the
 *                copper size it stands in for, and the schedule says so.
 *
 * A run stored before materials existed has none; it is COPPER, the one
 * material the stock report assumed until then (`pipeMaterialOf`).
 */
export const PLAN_PIPE_MATERIALS = ["copper", "pex-barrier", "multilayer"] as const;
export type PlanPipeMaterial = (typeof PLAN_PIPE_MATERIALS)[number];

export const DEFAULT_PIPE_MATERIAL: PlanPipeMaterial = "copper";

export const PIPE_MATERIAL_NAMES: Readonly<Record<PlanPipeMaterial, string>> = {
  copper: "Copper (EN 1057)",
  "pex-barrier": "PEX barrier",
  multilayer: "Multilayer",
};

/** The few letters a drawing prints after the size — nothing for copper, the default. */
export const PIPE_MATERIAL_MARKS: Readonly<Record<PlanPipeMaterial, string>> = {
  copper: "",
  "pex-barrier": "PEX",
  multilayer: "ML",
};

export interface PipeSpec {
  readonly diameterMm: PipeDiameterMm;
  readonly circuit: PipeCircuit;
  /** Absent on a run drawn before 2026-09-11: read it through `pipeMaterialOf` (copper). */
  readonly material?: PlanPipeMaterial;
}

/** A run's material: the stored one, or copper for a run drawn before materials existed. */
export function pipeMaterialOf(spec: { readonly material?: PlanPipeMaterial | null | undefined }): PlanPipeMaterial {
  return spec.material ?? DEFAULT_PIPE_MATERIAL;
}

/** "22 mm" for copper, "22 mm PEX barrier" otherwise — the size as a schedule prints it. */
export function pipeSizeLabel(diameterMm: number, material: PlanPipeMaterial): string {
  return material === "copper" ? `${diameterMm} mm` : `${diameterMm} mm ${PIPE_MATERIAL_NAMES[material]}`;
}

/**
 * Fittings a designer can draw. A reducer (added 2026-09-11) joins two sizes:
 * `diameterMm` is the larger, `reducedDiameterMm` the smaller. There is no
 * radiator-valve kind on purpose: the stock report already gives one TRV +
 * lockshield pair per radiator, and a drawn valve could only double-count
 * those or make the designer draw two on every radiator to get the same
 * figure.
 */
export const FITTING_KINDS = ["elbow", "tee", "coupling", "isolation-valve", "reducer"] as const;
export type FittingKind = (typeof FITTING_KINDS)[number];

export const FITTING_NAMES: Readonly<Record<FittingKind, string>> = {
  elbow: "Elbow",
  tee: "Tee",
  coupling: "Straight coupling",
  "isolation-valve": "Isolation valve",
  reducer: "Reducer",
};

export interface FittingSpec {
  readonly fitting: FittingKind;
  /** The pipe size it joins — taken from the pipe it was snapped onto, when it was. For a reducer, the larger size. */
  readonly diameterMm: PipeDiameterMm | null;
  /** A reducer's smaller size; absent (or null) for every other fitting, or while it is not known. */
  readonly reducedDiameterMm?: PipeDiameterMm | null;
  /** The material of the pipe it joins, taken from that pipe when snapped. Absent: not recorded. */
  readonly material?: PlanPipeMaterial;
}

/** "22 × 15 mm", "15 mm PEX barrier" — a fitting's size as a schedule prints it; null when not set. */
export function fittingSizeLabel(spec: FittingSpec): string | null {
  if (spec.diameterMm === null) return null;
  const size =
    spec.fitting === "reducer" && spec.reducedDiameterMm != null
      ? `${spec.diameterMm} × ${spec.reducedDiameterMm} mm`
      : `${spec.diameterMm} mm`;
  return spec.material && spec.material !== "copper" ? `${size} ${PIPE_MATERIAL_NAMES[spec.material]}` : size;
}

// ------------------------------------------------- cylinder and buffer --

/**
 * Indoor plant on the plan: the hot water cylinder and a buffer or
 * volumiser, each drawn at its footprint (a circle, its diameter) and counted
 * in the stock report and the system water volume. Added 2026-09-11 (research
 * note, "Gaps": "No cylinder or buffer object on the plan").
 *
 * LIKE THE REST OF THIS FILE, A LAYOUT DRAWING. The cylinder a design is sized
 * and certificated against is the one in the design decisions (MIS 3005-D
 * 3.5); one drawn here is where it goes and how big it is. The sizes offered
 * are NOMINAL — see `GENERIC_VESSEL_SOURCE` — and every caption says so:
 * confirm them with the product actually chosen.
 */
export const BUFFER_ROLES = ["buffer", "volumiser"] as const;
export type BufferRole = (typeof BUFFER_ROLES)[number];

export const BUFFER_ROLE_NAMES: Readonly<Record<BufferRole, string>> = {
  buffer: "Buffer",
  volumiser: "Volumiser",
};

export interface PlanCylinderSpec {
  /** `GENERIC_CYLINDERS` id for a size picked from the generic set; null for one entered by hand. */
  readonly genericId: string | null;
  readonly name: string;
  /** Nominal capacity, litres — the size a cylinder is ordered by. */
  readonly volumeLitres: number;
  readonly diameterMm: number;
  readonly heightMm: number;
  /**
   * Water held in the heat pump coil, litres — part of the heating circuit,
   * so it counts in the system volume. Null when not known.
   */
  readonly coilVolumeLitres: number | null;
}

export interface PlanBufferSpec {
  readonly genericId: string | null;
  readonly role: BufferRole;
  readonly name: string;
  /** Water it holds, litres — all of it on the heating circuit. */
  readonly volumeLitres: number;
  readonly diameterMm: number;
  readonly heightMm: number;
}

/** Bounds on a hand-entered cylinder or buffer. They catch typos (metres for mm), not products. */
export const VESSEL_LIMITS = {
  volumeLitres: { min: 5, max: 2000 },
  diameterMm: { min: 150, max: 1500 },
  heightMm: { min: 150, max: 3000 },
  coilVolumeLitres: { min: 0, max: 200 },
} as const;

export function litresText(litres: number): string {
  return Number.isInteger(litres) ? String(litres) : litres.toFixed(1);
}

/** "Ø550 × 1493 mm (nominal)" — a vessel's footprint and height as a caption prints them. */
export function vesselCaption(spec: Pick<PlanCylinderSpec, "diameterMm" | "heightMm" | "genericId">): string {
  return `Ø${Math.round(spec.diameterMm)} × ${Math.round(spec.heightMm)} mm${spec.genericId !== null ? " (nominal)" : ""}`;
}

/** An object's kind, what it is, and where it is — everything but its identity. */
export type PlanObjectShape =
  | { readonly kind: "heat-pump"; readonly spec: HeatPumpSpec; readonly geometry: PlacedGeometry }
  | { readonly kind: "radiator"; readonly spec: RadiatorSpec; readonly geometry: PlacedGeometry }
  | { readonly kind: "pipe"; readonly spec: PipeSpec; readonly geometry: RunGeometry }
  | { readonly kind: "fitting"; readonly spec: FittingSpec; readonly geometry: PlacedGeometry }
  | { readonly kind: "cylinder"; readonly spec: PlanCylinderSpec; readonly geometry: PlacedGeometry }
  | { readonly kind: "buffer"; readonly spec: PlanBufferSpec; readonly geometry: PlacedGeometry };

// ------------------------------------------------- outdoor unit catalogue --

/** Where a dimension came from. */
export interface DimensionSource {
  /** The exact URL the figure was read from. */
  readonly url: string;
  /** The document's own title. */
  readonly document: string;
  /** Revision, issue or date printed on the document, where it prints one. */
  readonly revision?: string;
  /** Page, table or section the figure is on. */
  readonly location?: string;
  /** ISO date the source was fetched. */
  readonly accessed: string;
  /** "manufacturer" for the maker's own document; "secondary" for anybody else's. */
  readonly kind: "manufacturer" | "secondary";
  /** Anything a reader of the figure should know — what the depth includes, say. */
  readonly note?: string;
}

/** A heat pump outdoor unit, as big as its manufacturer says it is. */
export interface OutdoorUnit {
  readonly id: string;
  readonly manufacturer: string;
  readonly range: string;
  /** The size on the box, as part of the name — "7 kW". Never a calculation input. */
  readonly sizeLabel: string;
  /** The manufacturer's model code(s) the dimensions are printed against. */
  readonly modelCodes: string;
  /** Casing width, mm — across the front. */
  readonly widthMm: number;
  /** Casing depth, mm — front to back. */
  readonly depthMm: number;
  readonly heightMm: number;
  readonly source: DimensionSource;
}

const ACCESSED = "2026-09-10";

type UnitRow = readonly [
  sizeLabel: string,
  modelCodes: string,
  widthMm: number,
  depthMm: number,
  heightMm: number,
];

function units(
  idPrefix: string,
  manufacturer: string,
  range: string,
  source: DimensionSource,
  rows: readonly UnitRow[],
): OutdoorUnit[] {
  return rows.map(([sizeLabel, modelCodes, widthMm, depthMm, heightMm]) => ({
    id: `${idPrefix}-${sizeLabel.replace(/ kW$/, "").replace(".", "_")}kw`,
    manufacturer,
    range,
    sizeLabel,
    modelCodes,
    widthMm,
    depthMm,
    heightMm,
    source,
  }));
}

/**
 * Outdoor units, W × D × H as the manufacturer prints them.
 *
 * Every row was read on 2026-09-10 from the document its `source` names, and
 * every document is the manufacturer's own. Where the source prints dimensions
 * in another order (H × W × D, "Length × width × height") the note says so
 * and the conversion is the only step between the page and this file. The
 * rows were then re-read against the fetched text of each document a second
 * time before this file was committed.
 *
 * Deliberately left out, for the reasons given:
 *   - Mitsubishi Ecodan R32 PUZ-WM50VHA and PUZ-HWM140: the data book prints
 *     their depth as "330+30" without saying what the 30 is.
 *   - Viessmann Vitocal 250-A: the only manufacturer sheet reached was a
 *     German-language one from a non-UK Viessmann site whose outdoor-unit row
 *     spans columns ambiguously; the UK page returned 404.
 *   - Daikin Altherma 3 H HT / Altherma 4, Samsung R32 Gen 6, NIBE F2040 and
 *     F2120: not reached. Add them the same way — from the manufacturer's own
 *     document, with its URL — rather than from memory.
 */
export const OUTDOOR_UNITS: readonly OutdoorUnit[] = [
  ...units(
    "vaillant-arotherm-plus",
    "Vaillant",
    "aroTHERM plus",
    {
      url: "https://professional.vaillant.co.uk/downloads/aproducts/renewables-1/arotherm-plus/arotherm-plus-spec-sheet-1892564.pdf",
      document: "aroTHERM plus specification sheet",
      revision: "VALTSB2BHTP0424",
      location: "Technical data — Product dimensions (H x W x D); page 1 dimensions table",
      accessed: ACCESSED,
      kind: "manufacturer",
      note:
        "Printed H x W x D. Depth is the casing's 450 mm from the technical data; page 1 gives " +
        "490 mm including the back plate and mounting brackets.",
    },
    [
      ["3.5 kW", "aroTHERM plus 3.5kW", 1100, 450, 765],
      ["5 kW", "aroTHERM plus 5kW", 1100, 450, 765],
      ["7 kW", "aroTHERM plus 7kW", 1100, 450, 965],
      ["10 kW", "aroTHERM plus 10kW", 1100, 450, 1565],
      ["12 kW", "aroTHERM plus 12kW", 1100, 450, 1565],
    ],
  ),
  ...units(
    "mitsubishi-ecodan-r290",
    "Mitsubishi Electric",
    "Ecodan R290",
    {
      url: "https://library.mitsubishielectric.co.uk/pdf/download_full/5483",
      document: "Ecodan ATW Databook (PUZ-WZ)",
      revision: "PUZ-WZ Vol.1.0",
      location: "Section A, outdoor unit specifications — Dimensions: Width / Depth / Height",
      accessed: ACCESSED,
      kind: "manufacturer",
      note: "kW size read from the model code (PUZ-WZ50 = 5 kW).",
    },
    [
      ["5 kW", "PUZ-WZ50VAA", 1050, 480, 1020],
      ["6 kW", "PUZ-WZ60VAA", 1050, 480, 1020],
      ["8 kW", "PUZ-WZ80VAA", 1050, 480, 1020],
      ["8.5 kW", "PUZ-WZ85VAA / PUZ-WZ85YAA", 1050, 480, 1040],
      ["10 kW", "PUZ-WZ100VAA / PUZ-WZ100YAA", 1050, 480, 1040],
      ["12 kW", "PUZ-WZ120VAA / PUZ-WZ120YAA", 1050, 480, 1040],
    ],
  ),
  ...units(
    "mitsubishi-ecodan-r32",
    "Mitsubishi Electric",
    "Ecodan R32",
    {
      url: "https://library.mitsubishielectric.co.uk/pdf/download_full/4099",
      document: "Ecodan ATW Data Book (R32)",
      revision: "Vol.5.3",
      location: "Section A, outdoor unit specifications — Dimensions: Width / Depth / Height",
      accessed: ACCESSED,
      kind: "manufacturer",
      note:
        "kW size read from the model code (PUZ-WM85 = 8.5 kW). An older data book than the R290 " +
        "one; confirm it is current before quoting.",
    },
    [
      ["6 kW", "PUZ-WM60VAA", 1050, 480, 1020],
      ["8.5 kW", "PUZ-WM85VAA / PUZ-WM85YAA", 1050, 480, 1020],
      ["11.2 kW", "PUZ-WM112VAA / PUZ-WM112YAA", 1050, 480, 1020],
    ],
  ),
  ...units(
    "daikin-altherma-3-m",
    "Daikin",
    "Altherma 3 M",
    {
      url: "https://my.daikin.eu/content/dam/document-library/catalogues/heat/air-to-water-heat-pump-low-temperature/ebla04ev3/Daikin%20Altherma%203%20M%204-8%20kW_Product%20catalogue_ECPEN22-764_English.pdf",
      document: "Daikin Altherma 3 M 4-8 kW product catalogue",
      revision: "ECPEN22-764 (04/22)",
      location: "Technical specifications — Casing, Dimensions (HeightxWidthxDepth)",
      accessed: ACCESSED,
      kind: "manufacturer",
      note: "Printed H x W x D: 770 x 1,250 x 362.",
    },
    [
      ["4 kW", "EBLA04E(3)V3 / EDLA04E(3)V3", 1250, 362, 770],
      ["6 kW", "EBLA06E(3)V3 / EDLA06E(3)V3", 1250, 362, 770],
      ["8 kW", "EBLA08E(3)V3 / EDLA08E(3)V3", 1250, 362, 770],
    ],
  ),
  ...units(
    "daikin-altherma-3-m",
    "Daikin",
    "Altherma 3 M",
    {
      url: "https://www.daikin.eu/content/dam/internet-denv/catalogues_brochures/residential/Daikin%20Altherma%203%20M_Product%20catalogue_ECPEN20-756_English.pdf",
      document: "Daikin Altherma 3 M product catalogue",
      revision: "ECPEN20-756 (11/20)",
      location: "Technical specifications — Casing, Dimensions (HeightxWidthxDepth)",
      accessed: ACCESSED,
      kind: "manufacturer",
      note:
        "Printed H x W x D: 870 x 1,380 x 460. kW size read from the model code. A 2020 " +
        "catalogue; confirm it is current before quoting.",
    },
    [
      ["9 kW", "EBLA09D(3)V3/W1 / EDLA09D(3)V3/W1", 1380, 460, 870],
      ["11 kW", "EBLA11D(3)V3/W1 / EDLA11D(3)V3/W1", 1380, 460, 870],
      ["14 kW", "EBLA14D(3)V3/W1 / EDLA14D(3)V3/W1", 1380, 460, 870],
      ["16 kW", "EBLA16D(3)V3/W1 / EDLA16D(3)V3/W1", 1380, 460, 870],
    ],
  ),
  ...units(
    "samsung-ehs-mono-r290",
    "Samsung",
    "EHS Mono R290",
    {
      url: "https://samsung-climatesolutions.com/gb/b2c/products/ehs/mono/r290.ae050cxydekeu.html",
      document: "Samsung Climate Solutions UK product page, EHS Mono R290 AE050CXYDEK (and AE080CXYDEK page)",
      location: 'Specifications — "Net Dimensions (WxHxD) (mm)": 998 x 850 x 500',
      accessed: ACCESSED,
      kind: "manufacturer",
      note:
        "From the manufacturer's web specification, which prints no revision; the installation " +
        "manual gives only bolt spacing, so the casing figure could not be cross-checked " +
        "against a document. 8 kW page: https://samsung-climatesolutions.com/gb/b2c/products/ehs/mono/r290.ae080cxydekeu.html",
    },
    [
      ["5 kW", "AE050CXYDEK", 998, 500, 850],
      ["8 kW", "AE080CXYDEK", 998, 500, 850],
    ],
  ),
  ...units(
    "samsung-ehs-mono-r290",
    "Samsung",
    "EHS Mono R290",
    {
      url: "https://samsung-climatesolutions.com/gb/b2c/products/ehs/mono/r290.ae120cxydekeu.html",
      document: "Samsung Climate Solutions UK product page, EHS Mono R290 AE120CXYDEK (and AE160CXYDEK page)",
      location: 'Specifications — "Net Dimensions (WxHxD) (mm)": 1270x1018x530',
      accessed: ACCESSED,
      kind: "manufacturer",
      note:
        "From the manufacturer's web specification, which prints no revision. 16 kW page: " +
        "https://samsung-climatesolutions.com/gb/b2c/products/ehs/mono/r290.ae160cxydekeu.html",
    },
    [
      ["12 kW", "AE120CXYDEK", 1270, 530, 1018],
      ["16 kW", "AE160CXYDEK", 1270, 530, 1018],
    ],
  ),
  ...units(
    "grant-aerona-290",
    "Grant",
    "Aerona 290",
    {
      url: "https://www.grantuk.com/media/2yubdwfa/aerona-290-datasheet_july-26.pdf",
      document: "Aerona 290 datasheet",
      revision: "GUKR290DS_REV0726.1",
      location: "Technical data table — HEIGHT / WIDTH / DEPTH (mm)",
      accessed: ACCESSED,
      kind: "manufacturer",
      note: "The 6.5 and 9 kW units really are wider (1223 mm) than the 12 and 15.5 kW units (1155 mm).",
    },
    [
      ["4 kW", "HPR2904", 1155, 422, 803],
      ["6.5 kW", "HPR29065", 1223, 461, 854],
      ["9 kW", "HPR2909", 1223, 461, 854],
      ["12 kW", "HPR29012", 1155, 425, 1365],
      ["15.5 kW", "HPR290155", 1155, 425, 1365],
    ],
  ),
  ...units(
    "nibe-s2125",
    "NIBE",
    "S2125",
    {
      url: "https://assetstore.nibe.se/hcms/v2.3/entity/document/905826/storage/OTA1ODI2LzAvbWFzdGVy",
      document: "NIBE S2125 installer manual",
      revision: "IHB EN 2525-1, 831880",
      location: "Chapter 11, Technical data — Dimensions and weight (pp. 49–50)",
      accessed: ACCESSED,
      kind: "manufacturer",
      note:
        "Depth 831 mm as printed; the dimensioned drawing is an image that was not read, so " +
        "whether it includes feet or connections is not confirmed.",
    },
    [
      ["8 kW", "S2125-8", 1128, 831, 1080],
      ["12 kW", "S2125-12", 1128, 831, 1080],
      ["16 kW", "S2125-16", 1278, 831, 1180],
      ["20 kW", "S2125-20", 1278, 831, 1180],
    ],
  ),
  ...units(
    "nibe-s2125",
    "NIBE",
    "S2125",
    {
      url: "https://assetstore.nibe.se/hcms/v2.3/entity/document/905779/storage/OTA1Nzc5LzAvbWFzdGVy",
      document: "NIBE S2125-14 installer manual",
      revision: "IHB EN 2525-1, 931059",
      location: "Technical data — Dimensions and weight",
      accessed: ACCESSED,
      kind: "manufacturer",
    },
    [["14 kW", "S2125-14 (3x400 V)", 1278, 831, 1180]],
  ),
  ...units(
    "viessmann-vitocal-150-a",
    "Viessmann",
    "Vitocal 150-A",
    {
      url: "https://www.viessmann.co.uk/content/dam/public-brands/gb/products/heat-pump/vitocal-151-a/Vitocal%20150-A_151-A%20brochure.pdf/_jcr_content/renditions/original./Vitocal%20150-A_151-A%20brochure.pdf",
      document: "Vitocal 150-A / 151-A brochure (UK)",
      revision: "07/2023 GB",
      location: 'Technical data — Dimensions, "Length x width x height", outdoor unit',
      accessed: ACCESSED,
      kind: "manufacturer",
      note:
        'Printed "Length x width x height" 600 x 1144 x 841 (1382 for the SP units). Taking 1144 ' +
        "as the width across the front and 600 as the depth is an inference — the same table's " +
        "wall-hung indoor unit (360 x 450 x 920) only reads sensibly with the first figure as " +
        "depth — not a printed statement. The footprint rectangle is the same either way. kW " +
        "size read from the model code.",
    },
    [
      ["4 kW", "Vitocal 150-A04", 1144, 600, 841],
      ["6 kW", "Vitocal 150-A06", 1144, 600, 841],
      ["8 kW", "Vitocal 150-A08", 1144, 600, 841],
      ["10 kW", "Vitocal 150-A10 SP", 1144, 600, 1382],
      ["13 kW", "Vitocal 150-A13 SP", 1144, 600, 1382],
      ["16 kW", "Vitocal 150-A16 SP", 1144, 600, 1382],
    ],
  ),
];

export function outdoorUnitById(id: string): OutdoorUnit | undefined {
  return OUTDOOR_UNITS.find((unit) => unit.id === id);
}

/** "Vaillant aroTHERM plus 7 kW" — the name a plan and a quote both use. */
export function outdoorUnitName(unit: OutdoorUnit): string {
  return `${unit.manufacturer} ${unit.range} ${unit.sizeLabel}`;
}

/** The spec a catalogue unit is placed with. */
export function heatPumpSpecFor(unit: OutdoorUnit): HeatPumpSpec {
  return {
    catalogueId: unit.id,
    name: outdoorUnitName(unit),
    widthMm: unit.widthMm,
    depthMm: unit.depthMm,
    heightMm: unit.heightMm,
  };
}

/** Bounds on a hand-entered ("custom") outdoor unit, mm. Catches typos, not products. */
export const CUSTOM_UNIT_LIMITS_MM = { min: 100, max: 3000 } as const;

// ------------------------------------------------------------- radiators --

/**
 * The radiator range the plan's sizes and depths come from.
 *
 * Purmo Compact, because it is the one range in `emitter-catalogue.ts` that
 * publishes an unambiguous depth per panel type (see `PURMO_COMPACT_DEPTH_MM`
 * there, and why both Stelrad ranges leave `depthMm` undefined). Depths differ
 * by a few millimetres between brands, so the plan says whose figure it is.
 */
export const RADIATOR_PLAN_RANGE = {
  id: "purmo-compact",
  brand: "Purmo",
  range: "Compact",
  url: PURMO_COMPACT.source.url ?? "",
  document: PURMO_COMPACT.source.standard,
  revision: PURMO_COMPACT.source.issue,
  accessed: PURMO_COMPACT.data.note.retrievedOn,
} as const;

export const RADIATOR_PANEL_TYPES: readonly PanelRadiatorType[] = ["K1", "P+", "K2", "K3"];

/** The trade's other name for each panel type — "a K2 is a Type 22". */
export const PANEL_TYPE_ALIASES: Readonly<Record<PanelRadiatorType, string>> = {
  K1: "Type 11",
  "P+": "Type 21",
  K2: "Type 22",
  K3: "Type 33",
};

interface RadiatorSize {
  readonly panelType: PanelRadiatorType;
  readonly heightMm: number;
  readonly lengthMm: number;
  readonly depthMm: number;
}

/** Every size the range is sold in, straight from the catalogue rows. */
const RADIATOR_SIZES: readonly RadiatorSize[] = PURMO_COMPACT.data.entries.flatMap((entry) =>
  entry.panelType !== undefined && entry.depthMm !== undefined
    ? [
        {
          panelType: entry.panelType,
          heightMm: entry.heightMm,
          lengthMm: entry.lengthMm,
          depthMm: entry.depthMm,
        },
      ]
    : [],
);

const ascending = (a: number, b: number): number => a - b;

/** The panel types the range actually offers, in trade order. */
export function radiatorPanelTypes(): readonly PanelRadiatorType[] {
  return RADIATOR_PANEL_TYPES.filter((type) => RADIATOR_SIZES.some((s) => s.panelType === type));
}

/** Heights the range offers in `panelType`, mm, ascending. */
export function radiatorHeights(panelType: PanelRadiatorType): readonly number[] {
  return [
    ...new Set(RADIATOR_SIZES.filter((s) => s.panelType === panelType).map((s) => s.heightMm)),
  ].sort(ascending);
}

/** Lengths the range offers in `panelType` at `heightMm`, mm, ascending. */
export function radiatorLengths(panelType: PanelRadiatorType, heightMm: number): readonly number[] {
  return [
    ...new Set(
      RADIATOR_SIZES.filter((s) => s.panelType === panelType && s.heightMm === heightMm).map(
        (s) => s.lengthMm,
      ),
    ),
  ].sort(ascending);
}

/**
 * The range's nominal depth for a panel type, mm — undefined for a type the
 * range does not sell. One figure per type: Purmo print one per type,
 * independent of height.
 */
export function radiatorDepthMm(panelType: PanelRadiatorType): number | undefined {
  return RADIATOR_SIZES.find((s) => s.panelType === panelType)?.depthMm;
}

/** The spec for a size the range sells; undefined for one it does not. */
export function radiatorSpecFor(
  panelType: PanelRadiatorType,
  heightMm: number,
  lengthMm: number,
): RadiatorSpec | undefined {
  const size = RADIATOR_SIZES.find(
    (s) => s.panelType === panelType && s.heightMm === heightMm && s.lengthMm === lengthMm,
  );
  if (!size) return undefined;
  return {
    range: RADIATOR_PLAN_RANGE.id,
    panelType,
    heightMm,
    lengthMm,
    depthMm: size.depthMm,
  };
}

/** "K2 600×1200" — the label a radiator carries on the plan. */
export function radiatorLabel(spec: Pick<RadiatorSpec, "panelType" | "heightMm" | "lengthMm">): string {
  return `${spec.panelType} ${spec.heightMm}×${spec.lengthMm}`;
}

// ------------------------------------------- generic cylinder and buffer --

/**
 * Where the generic cylinder and buffer sizes come from: ONE manufacturer's
 * published range, used as a typical footprint for each capacity — not a
 * product selection, and never named on the plan as that product. Read
 * 2026-09-11 from the text of the fetched PDF (pdftotext), pp. 14–15.
 */
export const GENERIC_VESSEL_SOURCE: DimensionSource = {
  url: "https://aws.tanks-direct.co.uk/files/pdf/2166_kingspan-albion-aerocyl-1054342-manual-en-gb-ie-aug2023-v6.pdf",
  document:
    "Kingspan Albion Aerocyl — Installation, Operation and Maintenance Manual: Unvented Heat Pump Cylinders with External Thermal Expansion and Buffer Tanks",
  revision: "Part No: 1054342 - AUG 2023 v6",
  location:
    'Technical Performance and Specification Data (pp. 14–15): "Heat pump unvented hot water cylinders" rows HP150–HP300 and ' +
    '"Buffer tank" rows HPB60–HPB210 — Total Height, Diameter, Actual Buffer Capacity, HP Coil Capacity',
  accessed: "2026-09-11",
  kind: "secondary",
  note:
    "Kingspan's own manual, but fetched from a retailer's file store, so marked secondary. The sizes are NOMINAL: " +
    "another make of the same capacity differs — confirm the footprint and coil volume with the product chosen.",
};

/** The nominal cylinder sizes offered, per `GENERIC_VESSEL_SOURCE` (HP150–HP300: Ø550 mm, HP coil 14.3 L). */
export const GENERIC_CYLINDERS: readonly PlanCylinderSpec[] = (
  [
    [150, 1117],
    [180, 1305],
    [210, 1493],
    [250, 1744],
    [300, 2057],
  ] as const
).map(([litres, heightMm]) => ({
  genericId: `cylinder-${litres}`,
  name: `${litres} L heat pump cylinder`,
  volumeLitres: litres,
  diameterMm: 550,
  heightMm,
  coilVolumeLitres: 14.3,
}));

/**
 * The nominal buffer sizes offered, per `GENERIC_VESSEL_SOURCE`: the size it
 * is sold as, the water it actually holds ("Actual Buffer Capacity"), and its
 * footprint. The water held is what the system volume counts.
 */
export const GENERIC_BUFFERS: readonly PlanBufferSpec[] = (
  [
    [60, 63, 475, 714],
    [90, 105, 475, 1089],
    [150, 151, 550, 1117],
    [180, 181, 550, 1305],
    [210, 211, 550, 1493],
  ] as const
).map(([nominal, litres, diameterMm, heightMm]) => ({
  genericId: `buffer-${nominal}`,
  role: "buffer" as BufferRole,
  name: `${nominal} L buffer`,
  volumeLitres: litres,
  diameterMm,
  heightMm,
}));

export function genericCylinderById(id: string): PlanCylinderSpec | undefined {
  return GENERIC_CYLINDERS.find((c) => c.genericId === id);
}

export function genericBufferById(id: string): PlanBufferSpec | undefined {
  return GENERIC_BUFFERS.find((b) => b.genericId === id);
}

/**
 * One line naming a vessel: "210 L heat pump cylinder · Ø550 × 1493 mm
 * (nominal)"; the litres are added when the name does not already say them
 * — "60 L buffer · holds 63 L · Ø475 × 714 mm (nominal)".
 */
export function vesselLabel(spec: PlanCylinderSpec | PlanBufferSpec): string {
  const litres = `${litresText(spec.volumeLitres)} L`;
  const named = ` ${spec.name}`.includes(` ${litres}`) || spec.name.includes(`(${litres}`);
  const holds = named ? "" : ` · holds ${litres}`;
  return `${spec.name}${holds} · ${vesselCaption(spec)}`;
}
